/** @jsx jsx */
import { useState, type ReactNode } from 'react';
import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';
import { Manager, Reference, Popper } from '@atlaskit/popper';
import { layers } from '@atlaskit/theme/constants';
import { fg } from '@atlassian/jira-feature-gating';

const styles = cssMap({
	root: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		color: token('color.text'),
		maxWidth: '210px',
		paddingBlock: token('space.200'),
		paddingInline: token('space.300'),
		position: 'absolute',
		zIndex: layers.modal(),
	},
});
const DisabledPopper = ({
	children,
	disabledPopperMessage,
}: {
	children: ReactNode;
	disabledPopperMessage: string;
}) => {
	const [isVisible, setIsVisible] = useState(false);
	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<div
						ref={ref}
						onMouseEnter={() => setIsVisible(true)}
						onMouseLeave={() => setIsVisible(false)}
						onFocus={() => setIsVisible(true)}
						onBlur={() => setIsVisible(false)}
						// eslint-disable-next-line @atlassian/a11y/no-noninteractive-tabindex
						tabIndex={0}
						data-testid="filters.ui.filters.assignee.stateless.disabled-popper.disabled-popper-reference"
					>
						{children}
					</div>
				)}
			</Reference>
			{isVisible && (
				<Popper placement="bottom-end">
					{({ ref, style, placement }) => {
						const styleProps = { style };
						return (
							// Style is required for react-popper to apply positioning correctly (allows it to render to parent)
							<div ref={ref} {...styleProps} data-placement={placement} css={styles.root}>
								{disabledPopperMessage}
							</div>
						);
					}}
				</Popper>
			)}
		</Manager>
	);
};

export const ConditionalDisabledPopper = ({
	children,
	isDisabled,
	disabledPopperMessage,
}: {
	children: ReactNode;
	isDisabled: boolean;
	disabledPopperMessage?: string;
}) => {
	if (isDisabled && disabledPopperMessage && fg('list_lovability_improving_filters')) {
		return (
			<DisabledPopper disabledPopperMessage={disabledPopperMessage}>{children}</DisabledPopper>
		);
	}
	return children;
};
